import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { Container } from '../../packages/core/di/Container';
import { i18n } from '../i18n';
import type { 
  CreateProductUseCase, 
  UpdateProductUseCase, 
  FindProductsUseCase,
  CreateProductDTO,
  UpdateProductDTO,
  FindProductsDTO,
  ProductResponseDTO 
} from '../../packages/core/application';

export const useProductStore = defineStore('product', () => {
  // State
  const products = ref<ProductResponseDTO[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const selectedProduct = ref<ProductResponseDTO | null>(null);

  // Getters
  const activeProducts = computed(() => products.value.filter(p => p.isActive));
  const productById = computed(() => (id: string) => products.value.find(p => p.id === id));
  const productsByCategory = computed(() => (category: string) => 
    products.value.filter(p => p.category === category)
  );

  // Actions
  const container = Container.getInstance();

  const fetchProducts = async (dto?: FindProductsDTO) => {
    loading.value = true;
    error.value = null;
    console.log('Fetching products with DTO:', dto);
    
    try {
      const findProductsUseCase = container.get<FindProductsUseCase>('FindProductsUseCase');
      products.value = await findProductsUseCase.execute(dto || { activeOnly: true });
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.fetchProducts');
      console.error('Error fetching products:', err);
    } finally {
      loading.value = false;
    }
  };

  // TODO: review use case not found 
  const createProduct = async (dto: CreateProductDTO) => {
    loading.value = true;
    error.value = null;
    
    try {
      const createProductUseCase = container.get<CreateProductUseCase>('CreateProductUseCase');
      const newProduct = await createProductUseCase.execute(dto);
      products.value.push(newProduct);
      return newProduct;
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.createProduct');
      console.error('Error creating product:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateProduct = async (id: string, dto: UpdateProductDTO) => {
    loading.value = true;
    error.value = null;
    
    try {
      const updateProductUseCase = container.get<UpdateProductUseCase>('UpdateProductUseCase');
      const updatedProduct = await updateProductUseCase.execute(id, dto);
      
      const index = products.value.findIndex(p => p.id === id);
      if (index !== -1) {
        products.value[index] = updatedProduct;
      }
      
      if (selectedProduct.value?.id === id) {
        selectedProduct.value = updatedProduct;
      }
      
      return updatedProduct;
    } catch (err) {
      error.value = err instanceof Error ? err.message : i18n.global.t('errors.updateProduct');
      console.error('Error updating product:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const selectProduct = (product: ProductResponseDTO | null) => {
    selectedProduct.value = product;
  };

  const clearError = () => {
    error.value = null;
  };

  const reset = () => {
    products.value = [];
    loading.value = false;
    error.value = null;
    selectedProduct.value = null;
  };

  return {
    // State
    products,
    loading,
    error,
    selectedProduct,
    
    // Getters
    activeProducts,
    productById,
    productsByCategory,
    
    // Actions
    fetchProducts,
    createProduct,
    updateProduct,
    selectProduct,
    clearError,
    reset
  };
});
